import { type CSSResultGroup, LitElement, unsafeCSS } from 'lit'
import { html, unsafeStatic } from 'lit/static-html.js'
import { customElement, property, query } from 'lit/decorators.js'
import { classMap } from 'lit/directives/class-map.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { ContextConsumer } from '@lit/context'
import { navbarContext, type NavbarPart } from '../context'
import { $t, localized } from '@/navbar/i18n'
import { emit } from '@/navbar/utils/emit'
import styles from './eco-suite-navbar-menu-item.scss?inline'
import type { Icon } from '@/navbar/navbar.types'

/**
 * @summary An individual item within a navbar menu, rendered as a button or anchor.
 * @tag eco-suite-navbar-menu-item
 * @slot label - The item's label text, overriding the label property.
 * @slot suffix - Trailing content rendered after the label, e.g. badges.
 * @event eco-click - Fired when the item is activated by mouse or keyboard.
 */
@localized()
@customElement('eco-suite-navbar-menu-item')
export class EcoSuiteNavbarMenuItem extends LitElement {
  /** @internal */ static readonly tagName = 'eco-suite-navbar-menu-item'
  /** @internal */ static readonly shadowRootOptions = { ...LitElement.shadowRootOptions, delegatesFocus: true }
  /** @internal */ static readonly styles: CSSResultGroup = [unsafeCSS(styles)]
  /** @internal */
  private navbarContextConsumer: ContextConsumer<{ __context__: NavbarPart }, this> = new ContextConsumer(this, {
    context: navbarContext,
    subscribe: true,
  })

  /** @internal */
  @query('.EcoSuiteNavbarMenuItem')
  private readonly control!: HTMLElement

  /** Menu item icon. */
  @property()
  icon?: Icon

  /** Menu item label. */
  @property()
  label?: string

  /** Menu item link href. */
  @property()
  href?: string

  /** Menu item link target. */
  @property()
  target?: '_blank' | '_self'

  /** Whether the menu item links to an external page. */
  @property({ type: Boolean, attribute: 'is-external' })
  isExternal = false

  /**
   * Menu item click handler. Invoked alongside the `eco-click` event.
   * Call `event.preventDefault()` within the handler to prevent the menu from closing on click.
   */
  @property({ attribute: false })
  onClick?: (e: MouseEvent) => void

  /** Whether the menu item is the current page. */
  @property({ type: Boolean, attribute: 'is-current' })
  isCurrent = false

  /**
   * @internal
   * Storybook only
   * */
  @property({ attribute: false })
  forceNavbarContext?: NavbarPart

  /** @internal */
  get tag() {
    return unsafeStatic(this.href ? 'a' : 'button')
  }

  /** @internal */
  get navbarContext() {
    return this.forceNavbarContext ?? this.navbarContextConsumer.value
  }

  /** Trigger a click on the inner button/link */
  public click() {
    this.control.click()
  }

  /** @internal */
  private handleClick(e: MouseEvent) {
    this.onClick?.(e)

    if (!e.defaultPrevented) {
      emit(this, 'eco-click')
    }
  }

  render() {
    const externalLabel = this.isExternal && this.label
      ? `${this.label}, ${$t('navbar.opensInNewTab')}`
      : undefined

    const isExternalTab = this.target === '_blank'

    return html`
      <${this.tag}
        class=${classMap({
          EcoSuiteNavbarMenuItem: true,
          'EcoSuiteNavbarMenuItem--current': !!this.isCurrent,
          'EcoSuiteNavbarMenuItem--mobilePanel': this.navbarContext === 'mobile',
        })}
        href=${ifDefined(this.href)}
        target=${ifDefined(this.target)}
        rel=${ifDefined(isExternalTab ? 'noopener noreferrer' : undefined)}
        aria-current=${ifDefined((this.isCurrent && this.href?.length) ? 'page' : undefined)}
        aria-label=${ifDefined(externalLabel)}
        @click=${this.handleClick}
      >
        ${
          this.icon
            ? html`
              <pwc-icon-shadow
                icon=${this.icon}
                size="sm"
                aria-hidden="true"
              ></pwc-icon-shadow>`
            : ''
        }
        <div class="EcoSuiteNavbarMenuItem__label">
          <slot name="label">
            ${this.label}
          </slot>
        </div>
        <div class="EcoSuiteNavbarMenuItem__suffix">
          <slot name="suffix"></slot>
        </div>
        ${
          this.isExternal
            ? html`<pwc-icon-shadow icon="external-link" size="xs" aria-hidden="true"></pwc-icon-shadow>`
            : ''
        }
      </${this.tag}>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'eco-suite-navbar-menu-item': EcoSuiteNavbarMenuItem
  }
}
